import { MobileOfferProvider } from '@numeo/types'
import { MobilePlan } from '@numeo/types-core'
import { ChartColumn, Crown, Gem, type LucideIcon } from 'lucide-react'
import { ButtonHTMLAttributes, ReactNode } from 'react'

export const offerFieldClass =
    'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-600 disabled:opacity-50'
export function OfferButton({
    variant = 'primary',
    className = '',
    ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost' | 'danger' }) {
    const colors = {
        primary: 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600',
        secondary:
            'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800',
        ghost: 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
        danger: 'bg-red-600 text-white hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600',
    }
    return (
        <button
            type="button"
            {...props}
            className={`inline-flex min-h-10 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900 disabled:cursor-not-allowed disabled:opacity-50 ${colors[variant]} ${className}`}
        />
    )
}
export function OfferSection({ title, children, description, action }: { title: string; children: ReactNode; description?: string; action?: ReactNode }) {
    return (
        <section className="min-w-0 space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <header className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="text-base font-semibold">{title}</h2>
                    {description && <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{description}</p>}
                </div>
                {action}
            </header>
            {children}
        </section>
    )
}
export function OfferNotice({ children, error = false }: { children: ReactNode; error?: boolean }) {
    return (
        <p role={error ? 'alert' : 'status'} className={error ? 'text-sm text-red-600 dark:text-red-400' : 'text-sm text-slate-600 dark:text-slate-400'}>
            {children}
        </p>
    )
}

// ── Retention tab ─────────────────────────────────────────────────────────────

/** Below this the iOS code chip turns amber so operators top up the batch before it runs dry. */
const LOW_CODES = 20
const codesLabel = (count: number) => `${count} ${count === 1 ? 'code' : 'codes'} left`

/** Per-plan identity on the Retention tab: icon, tint, badge and one-line purpose. */
export const planStyle: Record<
    MobilePlan,
    { Icon: LucideIcon; badge: string; tagline: string; card: string; tile: string; badgeClass: string; create: string }
> = {
    [MobilePlan.PRO_MOBILE]: {
        Icon: Crown,
        badge: 'Entry',
        tagline: 'Help Go users stay with a special offer.',
        card: 'border-blue-200 dark:border-blue-900',
        tile: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300',
        badgeClass: 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
        create: 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600',
    },
    [MobilePlan.ULTRA_MOBILE]: {
        Icon: ChartColumn,
        badge: 'Most popular',
        tagline: 'Keep Max users engaged with a personalized discount.',
        card: 'border-violet-200 dark:border-violet-900',
        tile: 'bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300',
        badgeClass: 'bg-violet-50 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
        create: 'bg-violet-100 text-violet-700 hover:bg-violet-200 dark:bg-violet-900/40 dark:text-violet-200 dark:hover:bg-violet-900/60',
    },
    [MobilePlan.PLATINUM_MOBILE]: {
        Icon: Gem,
        badge: 'Premium',
        tagline: 'Reduce churn and keep your highest value users.',
        card: 'border-amber-200 dark:border-amber-900',
        tile: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300',
        badgeClass: 'bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
        create: 'bg-amber-100 text-amber-800 hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:hover:bg-amber-900/60',
    },
}

function AppleMark() {
    return (
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" className="fill-current text-slate-900 dark:text-slate-100">
            <path d="M16.37 12.64c-.02-2.2 1.8-3.26 1.88-3.31-1.03-1.5-2.62-1.7-3.19-1.73-1.36-.14-2.65.8-3.34.8-.69 0-1.75-.78-2.88-.76-1.48.02-2.85.86-3.61 2.19-1.54 2.67-.39 6.62 1.11 8.79.73 1.06 1.6 2.25 2.75 2.21 1.1-.04 1.52-.71 2.85-.71 1.33 0 1.71.71 2.88.69 1.19-.02 1.94-1.08 2.66-2.15.84-1.23 1.19-2.42 1.21-2.48-.03-.01-2.32-.89-2.34-3.54ZM14.18 6.18c.6-.73 1.01-1.75.9-2.76-.87.04-1.92.58-2.54 1.31-.56.64-1.05 1.67-.92 2.66.97.08 1.96-.49 2.56-1.21Z" />
        </svg>
    )
}

function GooglePlayMark() {
    return (
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <path d="M3.6 2.3 13.3 12l-9.7 9.7c-.35-.2-.6-.6-.6-1.1V3.4c0-.5.25-.9.6-1.1Z" fill="#00D7FE" />
            <path d="M16.6 8.7 13.3 12 3.6 2.3c.1-.06.2-.1.32-.12.3-.06.62 0 .9.16l11.78 6.36Z" fill="#00F076" />
            <path d="M16.6 15.3 4.82 21.66c-.28.16-.6.22-.9.16-.12-.02-.22-.06-.32-.12L13.3 12l3.3 3.3Z" fill="#F43249" />
            <path d="m20.4 12.9-3.8 2.4-3.3-3.3 3.3-3.3 3.8 2.4c.7.44.7 1.36 0 1.8Z" fill="#FFC900" />
        </svg>
    )
}

export const storeMark: Record<MobileOfferProvider, () => JSX.Element> = { apple: AppleMark, google: GooglePlayMark }

/** The iOS code pool at a glance: grey when healthy, amber when low, red when empty. */
export function CodesChip({ codes, pending }: { codes: number; pending: boolean }) {
    // A batch still importing (right after the offer is created, or after "Generate codes").
    if (pending && codes === 0)
        return (
            <span className="inline-flex whitespace-nowrap rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                Generating codes…
            </span>
        )
    const tone =
        codes === 0
            ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
            : codes < LOW_CODES
              ? 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
              : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
    return (
        <span className={`inline-flex whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium ${tone}`}>
            {codes === 0 ? 'No codes yet' : codesLabel(codes)}
        </span>
    )
}

/** Plan status from its per-channel picks (iOS, Android, card). */
export function PlanStatus({ ios, android, card }: { ios: boolean; android: boolean; card: boolean }) {
    const on = [ios && 'iOS', android && 'Android', card && 'Card'].filter(Boolean)
    const [dot, label] = on.length === 3 ? ['bg-green-500', 'Active'] : on.length ? ['bg-amber-500', `${on.join(' · ')} only`] : ['bg-slate-400', 'No offer']
    return (
        <span className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium dark:border-slate-700">
            <span className={`h-2 w-2 rounded-full ${dot}`} aria-hidden="true" />
            {label}
        </span>
    )
}
